import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, HeartPulse, Building2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LandingPage = () => {
  const { affiliateSlug } = useParams();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAffiliate = async () => {
      try {
        const response = await axios.get(`${API_URL}/affiliates/${affiliateSlug}`);
        setAffiliate(response.data);
      } catch (error) {
        toast.error('Partner not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadAffiliate();
  }, [affiliateSlug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
      </div>
    );
  }

  if (!affiliate) return null;

  const isFitnessFlow = affiliate.flow_template === 'BOUTIQUE_FITNESS_V1';

  const handleStartApplication = () => {
    if (isFitnessFlow) {
      navigate(`/apply-fitness/${affiliateSlug}`);
    } else {
      navigate(`/apply/${affiliateSlug}`);
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${logoPath}`;
  };

  const affiliateLogo = getLogoUrl(affiliate.logo_url);
  const dark = affiliate.theme === 'dark';

  return (
    <div className={`min-h-screen ${dark ? 'bg-[#1A1A1A]' : 'bg-white'}`} style={{fontFamily: "'Inter', sans-serif"}}>
      <Helmet>
        <title>{affiliate.name ? `${affiliate.name} | Players Health Insurance` : 'Players Health | Sports & Fitness Insurance'}</title>
        <meta name="description" content={`Get specialized insurance coverage for your sports organization or fitness studio through ${affiliate.name || 'Players Health'}. Tailored quotes for youth sports, amateur leagues, and boutique fitness.`} />
      </Helmet>
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b ${dark ? 'bg-[#232323]/95 border-white/10' : 'bg-white/95 border-[#E5E7EB]'}`}>
        <div className="max-w-[1140px] mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={dark ? '/logo-light.png' : '/logo-dark.png'} alt="Players Health" className="h-9" />
            {affiliateLogo && (
              <>
                <div className="h-8 w-px bg-[#E5E7EB]"></div>
                <img src={affiliateLogo} alt={affiliate.name} className="h-9 max-w-[160px] object-contain" />
              </>
            )}
            {!affiliateLogo && affiliate.name && (
              <>
                <div className="h-8 w-px bg-[#E5E7EB]"></div>
                <span className="text-[#1A1A1A] font-bold text-sm uppercase tracking-wide" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
                  {affiliate.name}
                </span>
              </>
            )}
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#coverage" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Coverage</a>
            <a href="#mission" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Our Mission</a>
            <a href="mailto:support@playershealth.com" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Contact</a>
            <Button 
              onClick={handleStartApplication}
              className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-7 py-3 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/30 transition-all hover:-translate-y-0.5"
              style={{fontFamily: "'Barlow Condensed', sans-serif"}}
            >
              Get Quote
            </Button>
          </div>
        </div>
        {/* Gradient bottom border */}
        <div className="h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
      </nav>

      {/* Hero Section */}
      <section className={`relative min-h-[85vh] flex items-center overflow-hidden pt-[72px] ${dark ? 'bg-[#232323]' : 'bg-[#F7F8FA]'}`}>
        <div className={`absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[#6B5CE7] blur-[160px] ${dark ? 'opacity-[0.12]' : 'opacity-[0.06]'}`}></div>
        <div className={`absolute -bottom-36 -left-24 w-[500px] h-[500px] rounded-full bg-[#4ECDC4] blur-[160px] ${dark ? 'opacity-[0.12]' : 'opacity-[0.06]'}`}></div>

        <div className="relative z-10 max-w-[1140px] mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className={`${dark ? 'text-white' : 'text-[#1A1A1A]'} font-black uppercase leading-[0.95] mb-6`} style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(48px, 7vw, 80px)'}}>
                {isFitnessFlow ? 'Fitness' : 'Sports'} Insurance<br/>
                <span className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] bg-clip-text text-transparent">Made Easy</span>
              </h1>

              <p className={`${dark ? 'text-white/60' : 'text-[#555]'} text-lg max-w-[560px] mb-10 leading-relaxed`}>
                Players Health is here to help you get the best insurance package possible for your youth sports organization. Get your quote and purchase in minutes!
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleStartApplication}
                  data-testid="hero-cta-btn"
                  className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-8 py-4 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/35 transition-all hover:-translate-y-0.5"
                  style={{fontFamily: "'Barlow Condensed', sans-serif"}}
                >
                  Request a Quote Now <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#6B5CE7]/15">
                <img 
                  src="/hero-tennis.png" 
                  alt="Players Health Hero" 
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
      </section>

      {/* Partnership Banner — Accent Strip */}
      <section className={`relative py-12 ${dark ? 'bg-[#2A2A3D]' : 'bg-[#EEF0FF]'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="flex items-center gap-4">
              {affiliateLogo && (
                <img src={affiliateLogo} alt={affiliate.name} className="h-12 object-contain" />
              )}
              <div>
                <h2 className={`${dark ? 'text-white' : 'text-[#1A1A1A]'} font-black text-2xl lg:text-3xl uppercase leading-tight`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
                  PLAYERS HEALTH IS {affiliate.name.toUpperCase()}'S RECOMMENDED INSURANCE PARTNER
                </h2>
              </div>
            </div>
            <div>
              <p className={`${dark ? 'text-white/60' : 'text-[#555]'} leading-relaxed`}>
                We know sports. We know insurance. Let us handle all of your team's insurance needs. Fill out the form below and get a quote estimate in one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section id="mission" className={`py-24 ${dark ? 'bg-[#1A1A1A]' : 'bg-white'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="text-center max-w-[720px] mx-auto">
            <span className={`inline-block ${dark ? 'bg-[#2A2A3D] text-[#A78BFA]' : 'bg-[#EEF0FF] text-[#7B6FE8]'} font-bold text-[11px] uppercase tracking-[2px] px-4 py-1.5 rounded-md mb-5`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              Our Mission
            </span>
            <h2 className={`font-black ${dark ? 'text-white' : 'text-[#1A1A1A]'} uppercase mb-6`} style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
              Our Mission
            </h2>
            <div className="w-20 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] mx-auto rounded-full mb-8"></div>
            <p className={`${dark ? 'text-white/60' : 'text-[#555]'} text-base leading-relaxed`}>
              At Players Health, we're dedicated to protecting the wellbeing of athletes and organizations through comprehensive, tailored insurance solutions. We understand the unique risks in youth and amateur sports, and we're here to provide peace of mind so you can focus on what matters most—developing athletes and building strong communities.
            </p>
          </div>
        </div>
      </section>

      {/* Comprehensive Coverage Options */}
      <section id="coverage" className={`py-24 ${dark ? 'bg-[#232323]' : 'bg-[#F7F8FA]'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className={`inline-block ${dark ? 'bg-[#2A2A3D] text-[#A78BFA]' : 'bg-[#EEF0FF] text-[#7B6FE8]'} font-bold text-[11px] uppercase tracking-[2px] px-4 py-1.5 rounded-md mb-5`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              Coverage
            </span>
            <h2 className={`font-black ${dark ? 'text-white' : 'text-[#1A1A1A]'} uppercase mb-4`} style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
              Comprehensive Coverage Options
            </h2>
            <div className="w-20 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-[#7B6FE8]" />
              </div>
              <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>General Liability</h3>
              <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                Protection against bodily injury, property damage, and personal injury claims. Coverage limits from $1M to $5M available.
              </p>
            </div>

            <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                <HeartPulse className="h-7 w-7 text-[#4ECDC4]" />
              </div>
              <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Participant Accident</h3>
              <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                Medical coverage for athletes injured during practices, games, and travel. Primary and excess coverage options.
              </p>
            </div>

            <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                <Building2 className="h-7 w-7 text-[#7B6FE8]" />
              </div>
              <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Property & Equipment</h3>
              <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                Coverage for facilities, equipment, and other property owned or used by your sports organization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section — keeps dark accent for contrast */}
      <section id="cta" className="relative py-24 bg-[#232323] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B5CE7]/15 to-[#4ECDC4]/15"></div>
        
        <div className="relative z-10 max-w-[1140px] mx-auto px-6 text-center">
          <h2 className="text-white font-black uppercase mb-5" style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
            Ready to Protect<br/>
            <span className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] bg-clip-text text-transparent">Your Team?</span>
          </h2>
          <p className="text-white/60 max-w-lg mx-auto mb-10 leading-relaxed">
            Get a customized quote in one business day. Our team of experts will work with you to find the perfect coverage for your organization.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={handleStartApplication}
              data-testid="cta-btn"
              className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-8 py-4 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/35 transition-all hover:-translate-y-0.5"
              style={{fontFamily: "'Barlow Condensed', sans-serif"}}
            >
              Get Your Quote Now <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] py-8">
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-light.png" alt="Players Health" className="h-7" />
              <span className="text-white/60 text-sm">&copy; 2025 Players Health. All rights reserved.</span>
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">About</a>
              <a href="mailto:support@playershealth.com" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Contact</a>
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Privacy</a>
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
